import {Moment} from 'moment';
import PropTypes from 'prop-types';

import {TagSchema} from 'constants/RegistryConstants';
import {SequenceType} from 'constants/SequenceConstants';
import {SourceSummary} from 'constants/SourceConstants';
import {Party} from 'parties/PartyConstants';
import {REQUEST, Request} from 'requests/RequestConstants';
import APIUtil from 'util/APIUtil';
import {SOURCE_SUMMARY_SHAPE} from 'workflows/WorkflowConstants';

import {SourceSummaryGraphQL} from '../../graphql-fragments/nav/sources/__generated/SourceSummaryGraphQL';
import {ProcessTaskForCalendarGraphQL} from '../../graphql-fragments/processes/__generated/ProcessTaskForCalendarGraphQL';

export const MONTH_VIEW = 'MONTH_VIEW';
export const WEEK_VIEW = 'WEEK_VIEW';
export const DAY_VIEW = 'DAY_VIEW';
export const VIEWS = [DAY_VIEW, WEEK_VIEW, MONTH_VIEW];

export enum CalendarView {
  MONTH_VIEW = 'MONTH_VIEW',
  WEEK_VIEW = 'WEEK_VIEW',
  DAY_VIEW = 'DAY_VIEW',
}

export type ViewRange = [Moment, Moment];

export const CalendarPreferences = {
  PREFERRED_CALENDAR_VIEW: 'preferredCalendarView',
};

export interface CalendarPartyOptions {
  organizationId?: string;
  teamId?: string;
}

type SourceSummaryWithCollaborators = SourceSummary & {collaborators: Array<Party>};

export interface CalendarProject extends SourceSummaryWithCollaborators {
  date: Moment;
}

export interface ProjectColorMapping {
  [id: string]: string;
}

export interface CalendarFile {
  id: string;
  name: string;
  date: string; // Note: CalendarStore does NOT convert 'date' to a moment object for files
  deleted: boolean;
  wasModified: boolean;
  endAccess: string;
  source: SourceSummaryWithCollaborators | null;
  tagSchema: TagSchema | null;
  type: SequenceType | null;
}

// 'scheduledOn', similar to 'date' for entries, is originally a string replaced by a moment object when
// loaded into the CalendarStore.
export interface CalendarRequest extends Omit<Request, 'scheduledOn'> {
  scheduledOn: Moment;
  source: SourceSummary;
}

export interface CalendarEntryTask {
  notePartProvenanceId: number;
  completed: boolean;
  formats: object;
  indentation: number;
  text: string;
}

export interface CalendarEntry {
  id: string;
  sectionId: string;
  name: string;
  date: Moment;
  collaborators: Array<Party>;
  previewText: string;
  entryTasks: Array<CalendarEntryTask>;
  source: SourceSummaryWithCollaborators;
}

export type CalendarItem = CalendarEntry | CalendarRequest | CalendarProject | ProcessTaskForCalendarGraphQL;

export type NonProjectCalendarItem =
  | CalendarFile
  | CalendarEntry
  | CalendarRequest
  | ProcessTaskForCalendarGraphQL;

export function isCalendarFile(item: CalendarItem | CalendarFile): item is CalendarFile {
  return APIUtil.isFolderItem(item.id);
}

export function isCalendarRequest(item: CalendarItem | CalendarFile): item is CalendarRequest {
  return APIUtil.isRequest(item.id);
}

export function isCalendarProcessTask(
  item: CalendarItem | CalendarFile
): item is ProcessTaskForCalendarGraphQL {
  return APIUtil.isProcessTask(item.id);
}

export function isCalendarProject(item: CalendarItem): item is CalendarProject {
  return APIUtil.isSource(item.id);
}

export function getCalendarItemProject(
  item: NonProjectCalendarItem
): SourceSummary | SourceSummaryGraphQL | null {
  if (isCalendarProcessTask(item)) {
    return item.process.folder.source;
  }
  return item.source ?? null;
}

export function getCalendarItemProjectId(item: NonProjectCalendarItem): string | null {
  return getCalendarItemProject(item)?.id ?? null;
}

export interface CalendarItemsByDate<T extends CalendarFile | CalendarItem> {
  [date: string]: Array<T>;
}

export type LayeredCalendarItem<T extends CalendarItem> = T & {numDays: number};
export type LayeredCalendarItems<T extends CalendarItem> = Array<Array<LayeredCalendarItem<T>>>;

export type CalendarFilesByDate = CalendarItemsByDate<CalendarFile>;
export type CalendarRequestsByDate = CalendarItemsByDate<CalendarRequest>;
export type CalendarProcessTasksByDate = CalendarItemsByDate<ProcessTaskForCalendarGraphQL>;
export type CalendarEntriesByDate = CalendarItemsByDate<CalendarEntry>;
export type CalendarProjectsByDate = CalendarItemsByDate<CalendarProject>;

export interface InactiveCalendarInteraction {}

export interface DraggingCalendarInteraction {
  mouseDownDate: Moment;
  mouseOverDate: Moment;
}

export interface DragCompleteCalendarInteraction extends DraggingCalendarInteraction {
  mouseUpCoords: {x: number; y: number};
}

export interface SelectedItemCalendarInteraction {
  selectedCalendarItemDay: {id: string; date: Moment};
}

export type CalendarInteraction =
  | InactiveCalendarInteraction
  | DraggingCalendarInteraction
  | DragCompleteCalendarInteraction
  | SelectedItemCalendarInteraction;

export const CALENDAR_REQUEST_SHAPE = PropTypes.shape({
  ...REQUEST,
  scheduledOn: PropTypes.object.isRequired,
  source: SOURCE_SUMMARY_SHAPE,
});

export const CALENDAR_REQUESTS_SHAPE = PropTypes.objectOf(PropTypes.arrayOf(CALENDAR_REQUEST_SHAPE));

export const CALENDAR_ITEM_SHAPE = PropTypes.oneOfType([
  PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    collaborators: PropTypes.array,
    source: PropTypes.object,
  }),
  CALENDAR_REQUEST_SHAPE,
]);
